import { Editor } from '@tiptap/core';
import CharacterCount from '@tiptap/extension-character-count';
import { PluginKey } from '@tiptap/pm/state';
import { useEditor } from '@tiptap/react';
import { SuggestionOptions } from '@tiptap/suggestion';
import clsx from 'clsx';
import { Avatar, Body, Icon, IconType } from 'copilot-design-system';
import React from 'react';
import {
  useChannelStateContext,
  useMessageComposer,
  useMessageInputContext,
} from 'stream-chat-react';

import { SlashCommand as SuggestionCommand } from 'src/components/tiptap/custom-extensions/SlashCommand';
import { SlashMenuRenderer as MenuRenderer } from 'src/components/tiptap/custom-extensions/SlashMenuRenderer';
import { getMessageEditorExtensions } from 'src/components/tiptap/messageEditorExtensions';
import { RichTextInput } from 'src/components/tiptap/RichTextInput';
import {
  SuggestionMenu,
  SuggestionMenuItem,
} from 'src/components/tiptap/SuggestionMenu';
import { useInternalChatContext } from 'src/features/assistant/internalChatContext';
import { MAX_CHARACTERS_LIMIT } from 'src/features/channel/channel-content/ChannelContent';
import { useAppSelector } from 'src/hooks/useStore';
import { AssistantUserID } from 'src/store/clients/types';
import { selectCurrentUser } from 'src/store/storeUtils';
import { selectAllUsersMap } from 'src/store/users/types';
import { getInitials, getUserIdFromFullId } from 'src/utils/UserUtils';

const TOOLBAR_ACTIONS = [
  'bold',
  'italic',
  'underline',
  'bulletList',
  'orderedList',
  'link',
];
type CommandItem = SuggestionMenuItem<{
  label: string;
  icon: IconType;
  action: (editor: Editor, range: { from: number; to: number }) => void;
}>;

const COMMAND_ITEMS: CommandItem[] = [
  {
    id: 'giphy',
    label: 'Giphy',
    icon: 'API',
    action: (editor, range) => {
      editor
        .chain()
        .focus()
        .deleteRange({ from: range.from, to: range.to })
        .insertContent('/giphy ')
        .run();
    },
  },
];

const CommandOptions: React.FC<{
  editor: Editor;
  range: { from: number; to: number };
}> = ({ editor, range }) => {
  const handleSelect = React.useCallback(
    (item: CommandItem) => {
      item.action(editor, range);
    },
    [editor, range],
  );

  return (
    <SuggestionMenu
      items={COMMAND_ITEMS}
      onSelect={handleSelect}
      renderItem={(item) => (
        <>
          <Icon icon={item.icon} className="h-3 w-3 mr-2" />
          <Body>{item.label}</Body>
        </>
      )}
    />
  );
};

interface Props {
  content: string;
  placeholder?: string;
  handleSendMessage?: () => void;
  onContentChange: (editor: Editor) => void;
  editorRef?: (editor: Editor | null) => void;
}

type MentionItem = SuggestionMenuItem<{
  name: string;
  avatar?: string;
  fallbackColor?: string;
}>;

const MentionOptions: React.FC<{
  items: MentionItem[];
  editor: Editor;
  range: { from: number; to: number };
}> = ({ items, editor, range }) => {
  const { textComposer } = useMessageComposer();

  /**
   * This function is responsible for selecting a mention item
   * and inserting it into the editor.
   * It also updates the text composer with the selected item which is
   * needed to update the mentioned users state. Without this
   * the sent message won't have the mentioned users.
   */
  const handleSelect = React.useCallback(
    (item: MentionItem) => {
      const newMentionedUsers = textComposer.mentionedUsers.concat({
        id: item.id,
        name: item.name ?? '',
        fallbackColor: item.fallbackColor ?? '',
      });
      textComposer.setMentionedUsers(newMentionedUsers);
      const headPos = editor.state.selection.head;
      editor
        .chain()
        .focus()
        .deleteRange({ from: range.from, to: headPos })
        .insertContent(`@${item.name} `)
        .run();
    },
    [editor, range.from, textComposer],
  );

  return (
    <SuggestionMenu
      items={items}
      onSelect={handleSelect}
      renderItem={(item) => (
        <>
          <Avatar
            src={item.avatar ?? ''}
            alt={item.name ?? 'Avatar'}
            variant="circle"
            size="sm"
            fallbackColor={item.fallbackColor}
            text={getInitials(item.name ?? item.id)}
          />
          <Body>{item.name ?? item.id}</Body>
        </>
      )}
    />
  );
};

/**
 * isComposing prevents double submissions in Korean and other languages.
 * starting point for a read:
 * https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/isComposing
 * In the long term, the fix should happen by handling keypress, but changing this has unknown implications.
 */
const defaultShouldSubmit = (event: KeyboardEvent) =>
  event.key === 'Enter' && !event.shiftKey && !event.isComposing;

export const MessageRichTextInput: React.FC<Props> = ({
  content,
  placeholder,
  handleSendMessage,
  onContentChange,
  editorRef,
}) => {
  const allUsersMap = useAppSelector(selectAllUsersMap);

  const { channel } = useChannelStateContext();
  const { isInternalChat } = useInternalChatContext();
  const { onPaste } = useMessageInputContext();
  const currentUser = useAppSelector(selectCurrentUser);
  const getMentionItems: SuggestionOptions['items'] = ({
    query,
  }: {
    query: string;
  }) => {
    return (
      Object.values(channel?.state.members ?? {})
        // should not be able to mention yourself
        .filter((member) => member.user?.id !== currentUser?.id)
        .map((member) => {
          let name = member.user?.name ?? '';
          let avatar = member.user?.image ?? '';
          let fallbackColor = member.user?.fallbackColor ?? '';
          const id = getUserIdFromFullId(member.user?.id ?? '');
          const user = allUsersMap.get(id);
          if (user && member.user?.id !== AssistantUserID) {
            name =
              user.fields.givenName && user.fields.familyName
                ? `${user.fields.givenName} ${user.fields.familyName}`
                : '';
            avatar = user.fields.avatarImageUrl ?? '';
            fallbackColor = user.fields.fallbackColor ?? '';
          }

          return {
            id: member.user?.id ?? '',
            name,
            avatar,
            fallbackColor,
          };
        })
        .filter((member) =>
          member.name.toLowerCase().includes(query.toLowerCase()),
        )
    );
  };
  const tiptapEditor = useEditor({
    extensions: getMessageEditorExtensions({
      placeholder: placeholder ?? '',
      additionalExtensions: [
        CharacterCount.configure({
          limit: MAX_CHARACTERS_LIMIT,
        }),
        SuggestionCommand.configure({
          suggestion: {
            pluginKey: new PluginKey('mention'),
            char: '@',
            startOfLine: false, // trigger the mention autocomplete at any position in the text
            items: getMentionItems,
            render: () =>
              MenuRenderer({
                Component: MentionOptions,
                tippyProps: {
                  placement: 'bottom-start',
                },
              }),
          },
        }),
        SuggestionCommand.configure({
          suggestion: {
            pluginKey: new PluginKey('command'),
            char: '/',
            startOfLine: true, // only trigger the slash command autocomplete at the start of a line
            items: () => [],
            render: () =>
              MenuRenderer({
                Component: CommandOptions,
                tippyProps: {
                  placement: 'bottom-start',
                },
              }),
          },
        }),
      ],
    }),
    editorProps: {
      attributes: {
        class: clsx('flex flex-col gap-1 min-h-6 relative'),
      },
      handlePaste: (_, event) => {
        // Only delegate to stream-chat's onPaste for file uploads (e.g. pasted screenshots).
        // Text pastes must be handled by TipTap alone — stream-chat's onPaste asynchronously
        // calls textComposer.insertText which duplicates text already inserted by TipTap.
        const hasFiles = Array.from(event.clipboardData?.items ?? []).some(
          (item) => item.kind === 'file',
        );
        if (hasFiles) {
          onPaste(
            event as unknown as React.ClipboardEvent<HTMLTextAreaElement>,
          );
          return true;
        }
        return false;
      },
    },
    content,
    onCreate: ({ editor }) => onContentChange(editor),
    onUpdate: ({ editor }) => onContentChange(editor),
  });

  // Expose the editor instance to the parent component
  React.useEffect(() => {
    editorRef?.(tiptapEditor);
    return () => editorRef?.(null);
  }, [tiptapEditor, editorRef]);

  React.useEffect(() => {
    if (tiptapEditor && content !== tiptapEditor.getText()) {
      tiptapEditor.commands.setContent(content);
      tiptapEditor.commands.focus();
    }
  }, [tiptapEditor, content]);

  /**
   * We want to handle the enter key press to send the message
   * if the shift key is not pressed (there no modifier key pressed).
   *
   * We use a ref to store the latest handler to avoid stale closure issues.
   * The event listener is only set up once, but it always calls the latest handler.
   */
  const keyHandlerRef = React.useRef<(event: KeyboardEvent) => void>();
  keyHandlerRef.current = (event: KeyboardEvent) => {
    if (!tiptapEditor) return;

    if (defaultShouldSubmit(event) && tiptapEditor.isFocused) {
      event.preventDefault();
      // Block send when message exceeds character limit
      if (tiptapEditor.getText().length > MAX_CHARACTERS_LIMIT) {
        return;
      }
      handleSendMessage?.();
    }
  };

  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      keyHandlerRef.current?.(event);
    };
    document.addEventListener('keydown', handler, { capture: true });
    return () => {
      document.removeEventListener('keydown', handler, { capture: true });
    };
  }, []);

  return (
    <div className="relative flex flex-col gap-1 max-h-64 overflow-auto">
      {tiptapEditor && (
        <RichTextInput
          showHighlightToolbar={false}
          // we want to keep the assistant chat top toolbar hidden
          // user can still format the message content using keyboard shortcuts
          // but we don't show permanent toolbar actions
          showTopToolbar={!isInternalChat}
          editor={tiptapEditor}
          supportActions={{
            toolbar: TOOLBAR_ACTIONS,
          }}
          classes={{
            container: 'px-3 py-2',
            toolbar: 'rounded-tl-xl rounded-tr-xl',
          }}
        />
      )}
    </div>
  );
};
